import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, Star, MessageSquareHeart, Inbox } from "lucide-react";

function fmt(dateStr) {
  try {
    return new Date(dateStr).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return dateStr;
  }
}

const PAGE_LABEL = {
  "/": "Tableau de bord",
};

export default function FeedbackAdminView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/feedback");
        setData(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const items = data?.items || [];

  return (
    <div className="space-y-8 animate-in-up" data-testid="feedback-admin-view">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Espace admin</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Avis des testeurs</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">Retours envoyés par vos testeurs depuis le bouton « Donner mon avis ».</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-5">
            <div className="rounded-2xl border border-border bg-card p-6">
              <MessageSquareHeart className="w-6 h-6 text-primary" />
              <p className="font-heading text-3xl font-extrabold mt-3" data-testid="feedback-count">{data?.count ?? 0}</p>
              <p className="text-sm text-muted-foreground mt-0.5">Avis reçus</p>
            </div>
            <div className="rounded-2xl border border-ochre/30 bg-accent text-accent-foreground p-6">
              <Star className="w-6 h-6 fill-current text-ochre" />
              <p className="font-heading text-3xl font-extrabold mt-3" data-testid="feedback-average">{data?.average ?? 0}<span className="text-lg text-muted-foreground">/5</span></p>
              <p className="text-sm opacity-80 mt-0.5">Note moyenne</p>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground text-sm flex flex-col items-center gap-3">
              <Inbox className="w-8 h-8" />
              Aucun avis pour l'instant. Partagez l'app à vos testeurs et invitez-les à utiliser « Donner mon avis ».
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((it) => (
                <div key={it.id} data-testid={`feedback-item-${it.id}`} className="rounded-2xl border border-border bg-card p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`w-4 h-4 ${it.rating >= n ? "fill-current text-ochre" : "fill-none text-muted-foreground/30"}`} />
                        ))}
                      </div>
                      <p className="text-sm font-medium mt-2">{it.user_name || "Testeur"} <span className="text-muted-foreground font-normal">· {it.user_email}</span></p>
                    </div>
                    <span className="text-xs text-muted-foreground font-mono shrink-0">{fmt(it.created_at)}</span>
                  </div>
                  {it.message && <p className="text-sm mt-3 leading-relaxed">{it.message}</p>}
                  {it.page && (
                    <p className="text-[11px] text-muted-foreground mt-2 font-mono">depuis : {PAGE_LABEL[it.page] || it.page}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
